import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Target, Eye, Users, Zap } from "lucide-react";
import APSLogo from "../../../components/ui/APS Logo.svg";

const AboutPage = () => {
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Nav bar */}
      <header className="border-b border-surface-border px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img src={APSLogo} alt="APS Logo" className="h-11 w-auto object-contain" />
        </Link>
        <Link to="/" className="text-sm text-brand-muted hover:text-primary transition-colors">
          ← Back to home
        </Link>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-16">

        {/* Hero */}
        <div className="mb-14 text-center">
          <div className="inline-flex items-center justify-center mb-5">
            <img src={APSLogo} alt="AIPolicyScan" className="h-16 w-auto object-contain" />
          </div>
          <h1 className="text-4xl font-extrabold text-brand-text mb-4">About AIPolicyScan</h1>
          <p className="text-base text-brand-muted leading-relaxed max-w-xl mx-auto">
            We help compliance teams and businesses understand, track, and act on vendor privacy
            policies — automatically, at scale.
          </p>
        </div>

        {/* Mission */}
        <section className="mb-12">
          <h2 className="text-lg font-bold text-brand-text mb-3">Our Mission</h2>
          <p className="text-sm text-brand-muted leading-relaxed">
            Privacy compliance is broken. Procurement teams rely on PDFs, manual reviews, and
            legal opinions that go stale the moment a vendor updates their policy. AIPolicyScan
            exists to change that. We use AI to read, analyse, and monitor vendor privacy policies
            continuously — so your team always knows what your vendors are doing with data, without
            the hours of manual work.
          </p>
        </section>

        {/* Values */}
        <section className="mb-12">
          <h2 className="text-lg font-bold text-brand-text mb-5">What we stand for</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              {
                icon: Eye,
                title: "Transparency first",
                body: "We believe organisations have the right to understand exactly how their vendors handle data — clearly, not buried in legalese.",
              },
              {
                icon: Zap,
                title: "Automation with accountability",
                body: "AI accelerates analysis. But you stay in control — every output includes the source text so you can verify what we found.",
              },
              {
                icon: Users,
                title: "Customer data is sacred",
                body: "Your vendor list reflects your tech stack. We never use your data to build benchmarks, train models, or produce reports for others.",
              },
              {
                icon: Target,
                title: "Built for compliance teams",
                body: "We design every feature for the people who actually use it — DPOs, procurement leads, and GRC analysts — not just executives.",
              },
            ].map(({ icon: Icon, title, body }) => (
              <div
                key={title}
                className="bg-[#FAFAFA] rounded-2xl border border-surface-border px-5 py-5"
              >
                <div className="w-9 h-9 rounded-xl bg-[#ECE9FF] flex items-center justify-center mb-3">
                  <Icon className="w-4 h-4 text-[#5B4FCF]" />
                </div>
                <h3 className="text-sm font-bold text-brand-text mb-1">{title}</h3>
                <p className="text-sm text-brand-muted leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Contact CTA */}
        <section className="bg-[#F7F8FF] border border-[#E0DCFF] rounded-2xl px-6 py-7 text-center">
          <h2 className="text-base font-bold text-brand-text mb-2">Get in touch</h2>
          <p className="text-sm text-brand-muted mb-4">
            Questions, partnerships, or enterprise enquiries — we'd love to hear from you.
          </p>
          <a
            href="mailto:hello@aipolicyscan.ai"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#5B4FCF] text-white text-sm font-semibold hover:bg-[#4a40b8] transition-colors"
          >
            hello@aipolicyscan.ai
          </a>
        </section>

        {/* Footer note */}
        <div className="mt-12 pt-8 border-t border-surface-border text-center">
          <p className="text-xs text-brand-light">© 2026 AIPolicyScan. All rights reserved.</p>
          <div className="flex justify-center gap-5 mt-3">
            <Link to="/privacy-policy" className="text-xs text-brand-muted hover:text-primary transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-xs text-brand-muted hover:text-primary transition-colors">
              Terms
            </Link>
            <Link to="/" className="text-xs text-brand-muted hover:text-primary transition-colors">
              Back to Home
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AboutPage;
